/**
 * lp-cart.js — Panier Les Pierres du Bien-Être
 *
 * Cadeaux automatiques par paliers + cross-sell 1 clic.
 *
 * RÈGLES DE CONCEPTION (issues des pannes rencontrées) :
 * 1. Chargé depuis layout/theme.liquid, JAMAIS depuis une section : le panier
 *    se re-rend souvent et ré-exécuterait le script (écouteurs empilés → gel).
 * 2. Garde d'initialisation en plus, ceinture et bretelles.
 * 3. Un seul traitement à la fois + anti-rebond long : Shopify limite les
 *    requêtes rapprochées et répond alors du HTML au lieu du JSON.
 * 4. Aucun rafraîchissement déclenché si rien n'a changé → pas de boucle.
 */
(function () {
  'use strict';

  if (window.__lpCartInit) return;
  window.__lpCartInit = true;

  var DEBOUNCE_MS = 800;
  var MAX_RETRIES = 3;
  var cfg = null;
  var running = false;
  var pending = false;
  var timer = null;
  var failStreak = 0; // échecs consécutifs (requêtes refusées par Shopify)

  // --- Configuration ---------------------------------------------------------

  function readConfig() {
    var el = document.getElementById('lp-cart-config');
    if (!el) return null;

    var gifts = [];
    [1, 2].forEach(function (n) {
      var variantId = parseInt(el.dataset['gift' + n + 'Variant'], 10);
      var handle = el.dataset['gift' + n + 'Handle'];
      var threshold = parseInt(el.dataset['threshold' + n], 10);
      if (variantId && handle && threshold > 0) {
        gifts.push({ variantId: variantId, handle: handle, threshold: threshold });
      }
    });

    return {
      gifts: gifts,
      supplementHandle: el.dataset.supplementHandle || 'supplement-pierre-bracelet'
    };
  }

  // --- Accès panier (tolérant aux limitations Shopify) -----------------------

  function parseJson(response) {
    if (!response.ok) return null;
    return response.text().then(function (text) {
      try { return JSON.parse(text); } catch (e) { return null; }
    });
  }

  function getCart(attempt) {
    attempt = attempt || 0;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(parseJson)
      .catch(function () { return null; })
      .then(function (cart) {
        if (cart && cart.items) return cart;
        if (attempt >= 2) return null;
        return new Promise(function (resolve) {
          setTimeout(function () { resolve(getCart(attempt + 1)); }, 500 * (attempt + 1));
        });
      });
  }

  function postCart(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(parseJson).catch(function () { return null; });
  }

  // --- Calculs ---------------------------------------------------------------

  function isGift(handle) {
    return cfg.gifts.some(function (g) { return g.handle === handle; });
  }

  /** Articles réellement choisis par le client (hors suppléments et cadeaux) */
  function payingCount(cart) {
    return cart.items.reduce(function (acc, item) {
      if (item.handle === cfg.supplementHandle || isGift(item.handle)) return acc;
      return acc + item.quantity;
    }, 0);
  }

  /** Compteur affiché : tout sauf les suppléments (lignes cachées) */
  function displayCount(cart) {
    return cart.items.reduce(function (acc, item) {
      return acc + (item.handle === cfg.supplementHandle ? 0 : item.quantity);
    }, 0);
  }

  /**
   * Lignes "supplément pierre" devenues orphelines : leur bracelet a été
   * supprimé du panier. Elles sont masquées à l'affichage, donc si on ne les
   * retire pas le panier paraît vide tout en gardant un total et un compteur.
   */
  function orphanSupplements(cart) {
    var braceletGroups = Object.create(null);

    cart.items.forEach(function (item) {
      if (item.handle === cfg.supplementHandle) return;
      var group = item.properties && item.properties._bracelet_group;
      if (group) braceletGroups[group] = true;
    });

    return cart.items.filter(function (item) {
      if (item.handle !== cfg.supplementHandle) return false;
      var group = item.properties && item.properties._bracelet_group;
      // Groupe dont le bracelet a disparu → orpheline
      if (group) return !braceletGroups[group];
      // Sans groupe : orpheline seulement s'il ne reste aucun bracelet configuré
      return Object.keys(braceletGroups).length === 0;
    });
  }

  /** Liste des corrections à appliquer pour que les cadeaux collent aux paliers */
  function computeActions(cart) {
    var paying = payingCount(cart);
    var actions = [];

    cfg.gifts.forEach(function (gift) {
      var line = cart.items.find(function (item) { return item.handle === gift.handle; });
      var deserved = paying >= gift.threshold;

      if (deserved && !line) {
        actions.push({ type: 'add', label: gift.handle, gift: gift });
      } else if (!deserved && line) {
        actions.push({ type: 'remove', key: line.key, label: gift.handle });
      } else if (deserved && line && line.quantity > 1) {
        // Sécurité : un cadeau reste à l'unité
        actions.push({ type: 'quantity', key: line.key, label: gift.handle });
      }
    });

    // Suppléments sans bracelet : à supprimer, sinon le panier ne se vide jamais
    orphanSupplements(cart).forEach(function (line) {
      actions.push({ type: 'remove', key: line.key, label: 'supplément orphelin' });
    });

    return { paying: paying, actions: actions };
  }

  // --- Rafraîchissement du panier -------------------------------------------

  function refresh(itemCount) {
    var event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    event.action = 'update';
    event.promise = Promise.resolve({ detail: { itemCount: itemCount } });
    event.__lpInternal = true; // pour ne pas se ré-écouter
    document.dispatchEvent(event);
  }

  // --- Réconciliation --------------------------------------------------------

  function reconcile() {
    if (running) { pending = true; return; }
    cfg = readConfig();
    if (!cfg) return;

    running = true;

    getCart()
      .then(function (cart) {
        if (!cart) return false;

        var result = computeActions(cart);
        if (!result.actions.length) return false;

        console.log('[LP-CART] ' + result.paying + ' article(s) payant(s) →',
          result.actions.map(function (a) {
            return (a.type === 'add' ? '+' : a.type === 'remove' ? '-' : '=') + a.label;
          }).join(', '));

        // Une action après l'autre (jamais en parallèle : le panier se corromprait)
        var allOk = true;
        var chain = Promise.resolve();
        result.actions.forEach(function (action) {
          chain = chain.then(function () {
            var call;
            if (action.type === 'add') {
              call = postCart('/cart/add.js', { items: [{ id: action.gift.variantId, quantity: 1 }] });
            } else {
              call = postCart('/cart/change.js', {
                id: action.key,
                quantity: action.type === 'quantity' ? 1 : 0
              });
            }
            return call.then(function (res) {
              if (!res) {
                allOk = false;
                console.warn('[LP-CART] Action refusée (' + action.type + ' ' + action.label + '), nouvelle tentative prévue');
              }
            });
          });
        });

        return chain.then(function () { return { changed: true, allOk: allOk }; });
      })
      .then(function (outcome) {
        if (!outcome || outcome === false) {
          // État déjà cohérent : tout va bien
          failStreak = 0;
          return;
        }
        if (!outcome.changed) { failStreak = 0; return; }

        return getCart().then(function (cart) {
          if (cart) refresh(displayCount(cart));

          if (outcome.allOk) {
            failStreak = 0;
            // Passe de vérification : si tout est cohérent, elle ne fera rien
            schedule();
          } else if (failStreak < MAX_RETRIES) {
            // Shopify a refusé une requête (limitation) : réessayer un peu plus tard
            failStreak += 1;
            setTimeout(schedule, 1500 * failStreak);
          } else {
            console.warn('[LP-CART] Abandon après ' + MAX_RETRIES + ' tentatives — reprise au prochain événement panier');
            failStreak = 0;
          }
        });
      })
      .catch(function (e) { console.error('[LP-CART] Erreur:', e); })
      .finally(function () {
        running = false;
        if (pending) { pending = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  /**
   * Trois passes espacées après chaque mouvement du panier : la première peut
   * arriver avant que Shopify ait enregistré le changement (course de timing),
   * les suivantes rattrapent. Sans danger : une passe cohérente ne fait rien.
   */
  function scheduleWithFollowUps() {
    schedule();
    setTimeout(reconcile, 2500);
    setTimeout(reconcile, 6000);
  }

  // --- Cross-sell : ajout en 1 clic -----------------------------------------

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-lp-xsell-variant]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var variantId = parseInt(btn.dataset.lpXsellVariant, 10);
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    var comp = document.querySelector('cart-items-component');
    var payload = { items: [{ id: variantId, quantity: 1 }] };
    if (comp && comp.dataset.sectionId) {
      payload.sections = comp.dataset.sectionId;
      payload.sections_url = window.location.pathname;
    }

    postCart('/cart/add.js', payload)
      .then(function () { return getCart(); })
      .then(function (cart) {
        if (cart) refresh(displayCount(cart));
        schedule();
      })
      .finally(function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
  });

  // --- Déclencheurs ----------------------------------------------------------

  document.addEventListener('shopify:cart:lines-update', function (e) {
    if (e.__lpInternal) return;
    if (e.promise && typeof e.promise.then === 'function') {
      e.promise.then(scheduleWithFollowUps).catch(scheduleWithFollowUps);
    } else {
      scheduleWithFollowUps();
    }
  });

  document.addEventListener('quantity-selector:update', scheduleWithFollowUps);
  document.addEventListener('cart:update', scheduleWithFollowUps);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
